import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
    RULE_ARITHMETIC_FUNCTION_TYPES,
    RULE_DATE_KEY_TYPES,
    RULE_RELATIVE_DATE_TYPES,
} from 'yeself-common/enum/RuleEnum';

import SelectField from '../../components/SelectField';
import PlainStringField from '../../components/PlainStringField';

import { LastSeenGuideRuleValues } from '../types';

type LastSeenGuideRuleFieldProps = {
    value: LastSeenGuideRuleValues;
    onChange: (value: LastSeenGuideRuleValues) => void;
};

const LastSeenGuideRuleField = ({
    value,
    onChange,
}: LastSeenGuideRuleFieldProps): React.ReactElement => {
    const [levelValue, setLevelValue] = useState({
        level0: value.level?.[0]?.data.key || null,
        level1: value.level?.[1]?.data.key || null,
        level2: value.level?.[2]?.data.value || null,
    });

    const handleOnChange = useCallback(
        (index: number) => (newValue) => {
            const newLevelValue = { ...levelValue, [`level${index}`]: newValue };

            const level: LastSeenGuideRuleFieldProps['value']['level'] =
                newLevelValue.level1 === RULE_DATE_KEY_TYPES.CUSTOM_ABSOLUTE ||
                newLevelValue.level1 === RULE_DATE_KEY_TYPES.CUSTOM_RELATIVE
                    ? [
                          newLevelValue.level0 ? { data: { key: newLevelValue.level0 } } : undefined,
                          newLevelValue.level1 ? { data: { key: newLevelValue.level1 } } : undefined,
                          newLevelValue.level2 ? { data: { value: newLevelValue.level2 } } : undefined,
                      ]
                    : [
                          newLevelValue.level0 ? { data: { key: newLevelValue.level0 } } : undefined,
                          newLevelValue.level1 ? { data: { key: newLevelValue.level1 } } : undefined,
                      ];

            setLevelValue(newLevelValue);
            onChange({ ...value, level });
        },
        [levelValue, onChange, value]
    );

    return (
        <>
            <SelectField
                autoWidth
                options={[
                    {
                        label: <FormattedMessage id="Rules.level.after" />,
                        value: RULE_ARITHMETIC_FUNCTION_TYPES.GT,
                    },
                    {
                        label: <FormattedMessage id="Rules.level.afterOrEqual" />,
                        value: RULE_ARITHMETIC_FUNCTION_TYPES.GE,
                    },
                    {
                        label: <FormattedMessage id="Rules.level.before" />,
                        value: RULE_ARITHMETIC_FUNCTION_TYPES.LT,
                    },
                    {
                        label: <FormattedMessage id="Rules.level.beforeOrEqual" />,
                        value: RULE_ARITHMETIC_FUNCTION_TYPES.LE,
                    },
                    {
                        label: <FormattedMessage id="Rules.level.on" />,
                        value: RULE_ARITHMETIC_FUNCTION_TYPES.EQ,
                    },
                ]}
                value={levelValue.level0 || ''}
                onChange={handleOnChange(0)}
            />
            {levelValue.level0 ? (
                <SelectField
                    autoWidth
                    options={[
                        {
                            label: <FormattedMessage id="Rules.level.today" />,
                            value: RULE_DATE_KEY_TYPES.TODAY,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.yesterday" />,
                            value: RULE_DATE_KEY_TYPES.YESTERDAY,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.customAbsolute" />,
                            value: RULE_DATE_KEY_TYPES.CUSTOM_ABSOLUTE,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.customRelative" />,
                            value: RULE_DATE_KEY_TYPES.CUSTOM_RELATIVE,
                        },
                    ]}
                    value={levelValue.level1 || ''}
                    onChange={handleOnChange(1)}
                />
            ) : null}
            {levelValue.level1 === RULE_DATE_KEY_TYPES.CUSTOM_ABSOLUTE ? (
                <PlainStringField
                    type="date"
                    value={levelValue.level2 || ''}
                    onChange={handleOnChange(2)}
                />
            ) : null}
            {levelValue.level1 === RULE_DATE_KEY_TYPES.CUSTOM_RELATIVE ? (
                <SelectField
                    autoWidth
                    options={[
                        {
                            label: <FormattedMessage id="Rules.level.lastDay" />,
                            value: RULE_RELATIVE_DATE_TYPES.LAST_DAY,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.lastWeek" />,
                            value: RULE_RELATIVE_DATE_TYPES.LAST_WEEK,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.lastMonth" />,
                            value: RULE_RELATIVE_DATE_TYPES.LAST_MONTH,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.lastTwoMonths" />,
                            value: RULE_RELATIVE_DATE_TYPES.LAST_TWO_MONTHS,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.lastThreeMonths" />,
                            value: RULE_RELATIVE_DATE_TYPES.LAST_THREE_MONTHS,
                        },
                    ]}
                    value={levelValue.level2 || ''}
                    onChange={handleOnChange(2)}
                />
            ) : null}
        </>
    );
};

export default LastSeenGuideRuleField;
